<template>
  <div class="rp-wrap">

    <!-- 헤더 -->
    <div class="rp-header">
      <button class="rp-back-btn" @click="$emit('go-back')">
        <i class="bi bi-skip-backward-circle-fill text-warning"></i> 검색리스트
      </button>
      <div class="rp-header-title">
        <span class="rp-hotel-name">{{ hotelInfo.name }}</span>
        <span class="hotel-grade-badge" :class="hotelInfo.rating ? 'grade-has' : 'grade-none'">
          {{ hotelInfo.rating ? `${hotelInfo.rating}성급` : '등급없음' }}
        </span>
      </div>
    </div>

    <!-- 호텔 요약 -->
    <div class="rp-hotel-summary">
      <!-- ✅ 이미지 갤러리 그리드 -->
      <div class="rp-gallery-wrap">
        <!-- 메인 큰 이미지 -->
        <div class="rp-gallery-main" @click="openGallery(0)">
          <img v-if="images[0]" :src="images[0]" class="rp-gallery-main-img" :alt="hotelInfo.name" />
          <div v-else class="rp-hero-placeholder">🏨</div>
          <div class="rp-gallery-main-overlay">
            <span>🖼 사진 보기</span>
          </div>
        </div>

        <!-- 우측 썸네일 2×3 그리드 -->
        <div class="rp-gallery-grid">
          <div v-for="(img, i) in images.slice(1, 7)" :key="i" class="rp-gallery-thumb" @click="openGallery(i + 1)">
            <img :src="img" class="rp-gallery-thumb-img" :alt="`사진 ${i+2}`" />
            <!-- 마지막 썸네일에 총 개수 배지 -->
            <div v-if="i === 5 && images.length > 7" class="rp-gallery-more-badge">
              +{{ images.length - 7 }}장
            </div>
          </div>
        </div>
      </div>

      <!-- 호텔 메타 정보 -->
      <div class="rp-hotel-meta">
        <div class="rp-hotel-name-big">
          {{ hotelInfo.name }}
          <span class="rp-hotel-name-orig fw-bold">{{ hotelInfo.original_name }}</span>
        </div>
        <div class="rp-hotel-addr">
          <i class="bi bi-geo-alt text-danger"></i> {{ hotelInfo.address || hotelInfo.state_province || hotelInfo.city }}
        </div>
        <div v-if="hotelInfo.trustyou_rating" class="rp-rating-badge">
          <span class="rp-rating-score">{{ (hotelInfo.trustyou_rating / 10).toFixed(1) }}</span>
          <span class="rp-rating-label">TrustYou</span>
        </div>
        <div v-if="hotelInfo.description">
          <div :contenteditable="false" class="rp-hotel-desc" v-html="hotelInfo.description"></div>
        </div>        
      </div>
    </div>

    <!-- ✅ 전체화면 갤러리 모달 -->
    <div v-if="galleryOpen" class="rp-gallery-modal" @click.self="galleryOpen = false">
      <button class="rp-modal-close" @click="galleryOpen = false">✕</button>
      <button class="rp-modal-prev"  @click="prevGallery">‹</button>
      <div class="rp-modal-img-wrap">
        <img :src="galleryImages[galleryIndex]" class="rp-modal-img" />
        <div class="rp-modal-counter">{{ galleryIndex + 1 }} / {{ galleryImages.length }}</div>
      </div>
      <button class="rp-modal-next"  @click="nextGallery">›</button>
    </div>

    <!-- 호텔위치정보(구글맵) -->
    <div class="rp-map-wrap">
      <div id="rp-hotel-map" class="rp-hotel-map"></div>
    </div>

    <!-- 호텔부대시설 -->
    <div v-if="facilities.length" class="rp-facilities-wrap">
      <div class="rp-facilities-title">🏨 부대시설 · 서비스</div>
      <div class="rp-facilities-list">
        <span v-for="f in facilities" :key="f.facility_code" class="rp-facility-tag">
          {{ f.description_ko || f.description }}
        </span>
      </div>
    </div>


    <!-- 검색 조건 바 -->
    <div class="rp-condition-bar">
      <span class="rp-cond-item">📅 {{ searchData.check_in_date }} ~ {{ searchData.check_out_date }}</span>
      <span class="rp-cond-sep">|</span>
      <span class="rp-cond-item">🌙 {{ searchData.night_count }}박</span>
      <span class="rp-cond-sep">|</span>
      <span class="rp-cond-item">👥 성인 {{ searchData.adult_count }}명 · {{ searchData.room_count }}실</span>
      <span v-if="searchData.children?.length" class="rp-cond-item">
        · 어린이 {{ searchData.children.length }}명
      </span>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="rp-loading">
      <div class="rp-loading-inner">
        <div class="rp-loading-anim">
          <div class="ld-bar" v-for="n in 5" :key="n" :style="`animation-delay:${(n-1)*0.12}s`"></div>
        </div>
        <p>객실 · 플랜을 조회하고 있습니다...</p>
      </div>
    </div>

    <!-- 에러 -->
    <div v-else-if="errMsg" class="rp-error">
      <div class="rp-error-icon">⚠️</div>
      <p>{{ errMsg }}</p>
      <button class="rp-retry-btn" @click="loadRooms">다시 시도</button>
    </div>

    <!-- 결과 없음 -->
    <div v-else-if="!loading && rooms.length === 0" class="rp-empty">
      <div class="rp-empty-icon">😥</div>
      <h3>선택한 조건에 맞는 객실이 없습니다</h3>
      <p>날짜나 인원을 변경해 보세요.</p>
      <button class="rp-back-btn2" @click="$emit('go-back')">← 검색으로 돌아가기</button>
    </div>

    <!-- 객실/플랜 리스트 -->
    <div v-else class="rp-room-list">
      <div class="rp-result-count">
        <strong>{{ rooms.length }}</strong>개 객실 유형 · 총 <strong>{{ totalPlanCount }}</strong>개 플랜
      </div>
    <div v-for="room in rooms" :key="room.room_desc" class="rp-room-card">

  <!-- ✅ 3단 레이아웃: 이미지 | 객실정보 | 플랜목록 -->
  <div class="rp-room-body">

    <!-- 좌: 이미지 -->
    <div class="rp-room-img-col" @click="openRoomGallery(room.room_code)">
      <template v-if="roomImageMap[room.room_code]?.length">
        <img :src="roomImageMap[room.room_code][0]"
            class="rp-room-img" :alt="room.room_desc"
            @error="e => e.target.style.display='none'" />
        <div class="rp-room-img-count" @click.stop>
          <i class="bi bi-images"></i> {{ roomImageMap[room.room_code].length }}장
        </div>
      </template>
      <template v-else-if="images[0]">
        <img :src="images[0]" class="rp-room-img" :alt="room.room_desc"
            @click.stop="openGallery(0)" />
      </template>
      <div v-else class="rp-room-img-placeholder">🛏</div>
    </div>

    <!-- 중: 객실 정보 -->
    <div class="rp-room-info-col">
      <div class="rp-room-name">{{ room.room_desc }}</div>
      <div v-if="room.supplier_desc" class="rp-room-bed-type">
        <i class="bi bi-moon-stars-fill text-primary"></i> {{ room.supplier_desc }}
      </div>

      <!-- 객실 속성 태그 -->
      <div class="rp-room-attrs">
        <span v-if="room.size_value" class="rp-attr">
          <i class="bi bi-aspect-ratio"></i> {{ room.size_value }}{{ room.size_unit }}
        </span>
        <span v-if="room.view_code" class="rp-attr">
          <i class="bi bi-binoculars"></i> {{ room.view_code }}
        </span>
        <span v-if="room.floor" class="rp-attr">
          <i class="bi bi-building"></i> {{ room.floor }}층
        </span>
        <span v-if="room.non_smoking === 1" class="rp-attr rp-attr-smoking">
          <i class="bi bi-slash-circle"></i> 금연
        </span>
        <span v-if="room.non_smoking === 0" class="rp-attr">
          <i class="bi bi-emoji-smoke"></i> 흡연가능
        </span>
      </div>

      <!-- 침대 정보 -->
      <div v-if="Object.keys(room.beds || {}).length" class="rp-room-beds">
        <span v-for="(cnt, type) in room.beds" :key="type" class="rp-bed-tag">
          🛏 {{ type }} × {{ cnt }}
        </span>
      </div>

      <!-- 플랜 수 요약 -->
      <div class="rp-plan-count-badge">
        {{ room.plans.length }}개 플랜 선택 가능
      </div>
    </div>

    <!-- 우: 플랜 목록 -->
    <div class="rp-room-plans-col">
      <div v-for="(plan, pi) in room.plans" :key="pi" class="rp-plan-row">

        <!-- 플랜 태그 -->
        <div class="rp-plan-tags">
          <span v-if="plan.food_code" class="plan-tag pt-food">
            🍽 {{ FOOD[plan.food_code] || '' }}
          </span>
          <span :class="plan.non_refundable ? 'plan-tag pt-nr' : 'plan-tag pt-r'">
            {{ plan.non_refundable ? '환불불가' : '환불가능' }}
          </span>
          <span v-if="plan.cancel_summary" class="plan-tag pt-cancel">
            📋 {{ plan.cancel_summary }}
          </span>
          <!-- ✅ 추가 -->
          <span v-if="plan.is_min_enforced" class="plan-tag pt-min">
            ⚠️ 최소판매가 적용
          </span>
        </div>

        <div v-if="plan.extra_usd > 0" class="rp-plan-extra">
          세금·리조트피 USD {{ plan.extra_usd.toFixed(2) }} 별도
        </div>

        <!-- 가격 -->
        <div class="rp-plan-price-wrap">
          <div class="rp-plan-cost">
            <span class="rp-cost-label">제공가</span>
            <span class="rp-cost-val">{{ krw(plan.cost_krw) }}원</span>
          </div>
          <div class="rp-plan-selling">
            {{ krw(plan.selling_krw) }}<small>원</small>
          </div>
          <div class="rp-plan-per-night">
            1박 약 {{ krw(Math.round(plan.selling_krw / searchData.night_count)) }}원
          </div>
          <div class="rp-plan-margin-line">
            마진 <strong>+{{ krw(plan.selling_krw - plan.cost_krw) }}원</strong>
          </div>
          <button class="rp-book-btn" @click="selectPlan(plan)">
            이 플랜 예약 →
          </button>
        </div>

      </div>
    </div>

  </div>
</div>
</div>

  </div>
</template>

<script>
// import { GoogleMap, Marker } from 'vue3-google-map'

export default {
  name: 'RtxRoomPlan',

  props: {
    hotelInfo:  { type: Object, required: true },
    searchData: { type: Object, required: true },
    marginMap:  { type: Object, default: () => ({}) },
  },

  emits: ['select-plan', 'go-back'],

  FOOD: {
    1:'룸온리', 2:'조식포함', 3:'점심포함',
    4:'저녁포함', 5:'하프보드', 6:'풀보드', 7:'올인클루시브'
  },

  data() {
    return {
      loading: false,
      errMsg:  '',
      rooms:   [],   // [{ room_desc, supplier_desc, plans:[...] }]
      galleryOpen:    false,
      galleryIndex:   0,
      images:         [],
      imgLoading:     false,

      roomImageMap: {},         // { room_code: [url1, url2, ...] } 객실정보
      galleryImages: [],        // 호텔제공이미지

      mapLoading: false,
      facilities: [],           //  호텔부대시설
    };
  },

  computed: {
    user()             { return this.$store.state.user; },
    
    FOOD()          { return this.$options.FOOD; },
    rakutenImageHost() { return this.$store.state.rakutenImageHost || ''; },

    heroImg() {
      if (!this.hotelInfo?.heroImg) return '';
      return `${this.rakutenImageHost}/${this.hotelInfo.hotel_id}/${this.hotelInfo.heroImg}`;
    },

    totalPlanCount() {
      return this.rooms.reduce((s, r) => s + (r.plans?.length || 0), 0);
    }
  },

  created() {
    Promise.all([
      this.loadRooms(),
      this.loadAllImages(),
      this.loadRoomImages(),       //객실이미지와 정보취득
      this.loadFacilities()        //호텔부대시설
    ]);

    // console.log('facilities:', this.hotelInfo.facilities);
  },

  mounted() {
    this.loadGoogleMaps();
  },

  methods: {
    loadGoogleMaps() {
      // 이미 로드된 경우
      if (window.google?.maps) {
        this.initMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_API_KEY}&language=ko`;
      script.async = true;
      script.defer = true;
      script.onload = () => this.initMap();
      document.head.appendChild(script);
    },

    openGallery(idx = 0) {
      this.galleryImages = this.images;  // ✅ 호텔 이미지
      this.galleryIndex  = idx;
      this.galleryOpen   = true;
    },

    prevGallery() {
      const len = this.galleryImages.length;
      this.galleryIndex = (this.galleryIndex - 1 + len) % len;
    },

    nextGallery() {
      this.galleryIndex = (this.galleryIndex + 1) % this.galleryImages.length;
    },

    openRoomGallery(roomCode) {
      const imgs = this.roomImageMap[roomCode];
      if (!imgs?.length) return;
      this.galleryImages = imgs;
      this.galleryIndex  = 0;
      this.galleryOpen   = true;
    },

    // 호텔위치맵(구글)
    initMap() {
      if (!this.hotelInfo.latitude || !this.hotelInfo.longitude) return;
      if (this.mapLoaded) return;

      const lat = parseFloat(this.hotelInfo.latitude);
      const lng = parseFloat(this.hotelInfo.longitude);

      const map = new window.google.maps.Map(document.getElementById('rp-hotel-map'), {
        center: { lat, lng },
        zoom: 15,
        mapTypeControl:    false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: this.hotelInfo.name,
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
        }
      });

      new window.google.maps.InfoWindow({
        content: `
          <div style="font-size:13px; font-weight:700; padding:4px 8px;">
            ${this.hotelInfo.name}<br>
            <span style="font-size:11px; color:#666;">${this.hotelInfo.address || ''}</span>
          </div>
        `
      }).open(map);

      this.mapLoaded = true;
    },

    // 호텔부대시설
    async loadFacilities() {
      // 빈배열이라도 []도 값을 가진 것으로 간주하여 3개 미만이면 로드하지 않음
      if (!this.hotelInfo.facilities || this.hotelInfo.facilities.length < 3)
      {
        return false;
      }

      try 
      {
        const codes = typeof this.hotelInfo.facilities === 'string'
          ? JSON.parse(this.hotelInfo.facilities)
          : this.hotelInfo.facilities;

        const result = await this.$api('/api/rakutenFacilitiesByIds', {
          param: [codes]
        });
        this.facilities = result || [];
      } 
      catch(e) 
      {
        console.error('[RTX] 시설 로드 실패:', e);
      }
    },


    async loadRoomImages() {
      try {
        const result = await this.$api('/api/rakutenHotelRoomsAll', {
          param: [this.hotelInfo.hotel_id]
        });

        const newMap = {};
        (result || []).forEach(r => {
          if (!r.images) return;
          try {
            const imgs = typeof r.images === 'string'
              ? JSON.parse(r.images)
              : r.images;

            // ✅ title_translation을 키로, URL 그대로 사용
            if (r.title_translation) {
              newMap[r.title_translation] = (imgs || [])
                .map(img => img.url || '')
                .filter(Boolean);
            }
          } catch(e) {
            console.error('[RTX] 객실 이미지 파싱 실패:', r.room_code, e);
          }
        });

        this.roomImageMap = newMap;
        // console.log('roomImageMap:', this.roomImageMap);

      } catch(e) {
        console.error('[RTX] 객실 이미지 로드 실패:', e);
      }
    },

    async loadRooms() {
      this.loading = true;
      this.errMsg  = '';
      this.rooms   = [];

      try 
      {
        const result = await this.$api('/api/rtx/hotelRooms', {
          param: [{
            hotel_id:       this.hotelInfo.hotel_id,
            check_in_date:  this.searchData.check_in_date,
            check_out_date: this.searchData.check_out_date,
            room_count:     this.searchData.room_count,
            adult_count:    this.searchData.adult_count,
            children:       this.searchData.children || []
          }]
        });

        if (!result?.success) throw new Error(result?.message || '객실 조회에 실패했습니다.');

        // 응답 plans 배열을 room_desc 기준으로 그룹화
        const allPlans = result.plans || [];
        this.rooms = this.groupByRoom(allPlans);

      } 
      catch (e) 
      {
        console.error('[RTX RoomPlan] 오류:', e);
        this.errMsg = e.message;
      } 
      finally 
      {
        this.loading = false;
      }
    },

    // 페이지 상단 호텔요약용 호텔제공이미지
    async loadAllImages() {
      this.imgLoading = true;
      try 
      {
        const result = await this.$api('/api/rakutenHotelImages_all', {
          param: [this.hotelInfo.hotel_id]   // sql.js의 ? 에 바인딩
        });

        this.images = (result || []).map(img =>
          `${this.rakutenImageHost}/${this.hotelInfo.hotel_id}/${img.image_url}`
        );
      } 
      catch (e) 
      {
        console.error('[RoomPlan] 이미지 로드 실패:', e);
        this.images = [];
      } 
      finally 
      {
        this.imgLoading = false;
      }
    },


    groupByRoom(plans) {
      const margin = this.marginMap[this.hotelInfo.hotel_id] || {
        margin: this.hotelInfo.margin ?? 0,
        marginMethod: this.hotelInfo.marginMethod ?? 'A'
      };

      // console.log('plans[0]:', JSON.stringify(plans[0]));
      // console.log('marginMap:', this.marginMap[this.hotelInfo.hotel_id]);

      const map = {};
      plans.forEach(p => {
        const key = p.room_details?.description || p.room_details?.room_type || '기타';
        const taxUsd    = p.taxes_and_fees?.tax?.value    ?? 0;
        const resortUsd = p.hotel_fees?.resort_fee?.value ?? 0;
        const costKrw   = p.cost_krw;
        const minKrw    = p.minimum_selling_krw ?? null;  // ✅ 추가

        let selling = this.calcSelling(costKrw, margin);

        // ✅ minimum_selling_krw 하한선 보정
        if (minKrw && selling < minKrw) selling = minKrw;

        const planItem = {
          cost_krw:        costKrw,
          selling_krw:     selling,
          min_selling_krw: minKrw,                               // ✅ 추가
          is_min_enforced: !!(minKrw && selling === minKrw),     // ✅ 추가
          non_refundable:  p.room_details?.non_refundable,
          food_code:       p.room_details?.food ?? null,
          extra_usd:       taxUsd + resortUsd,
          cancel_summary:  this.cancelSummary(p.cancellation_policy),
          cancel_policy:   p.cancellation_policy || null,
          booking_key:     p.booking_key || '',
          rtxPackage:      p.rtxPackage,
          rtxSearch:       p.rtxSearch,
        };

        if (!map[key]) {
          map[key] = {
            room_desc:     p.room_details?.description          || key,
            supplier_desc: p.room_details?.supplier_description || '',
            room_code:     p.room_details?.description          || '',
            size_value:    p.room_details?.size_value  || null,
            size_unit:     p.room_details?.size_unit   || 'm2',
            view_code:     p.room_details?.view_code   || '',
            non_smoking:   p.room_details?.non_smoking ?? null,
            floor:         p.room_details?.floor       || null,
            beds:          p.room_details?.beds        || {},
            plans: []
          };
        }
        map[key].plans.push(planItem);
      });
      return Object.values(map);
    },

    calcSelling(costKrw, margin) {
      if (costKrw == null) return null;
      if (margin.marginMethod === 'P') return Math.round(costKrw * (1 + margin.margin / 100));
      if (margin.marginMethod === 'A') return Math.round(costKrw + margin.margin);
      return costKrw;
    },

    cancelSummary(policy) {
      if (!policy?.cancellation_policies?.length) return '';
      const paid = policy.cancellation_policies.find(p => p.penalty_percentage === 100);
      if (paid) {
        const d = new Date(paid.date_from);
        return `${d.getMonth() + 1}/${d.getDate()} 이전 무료취소`;
      }
      return policy.remarks || '';
    },

    selectPlan(plan) {
      // 테스트용 로직 -> 예약+결제 프로세스 완료 후 삭제
      if(this.user.handlerGrade > 4) {
        this.$swal({
          icon: 'error',
          title: '예약 불가',
          text: '예약 권한이 없습니다. 관리자에게 문의하세요.',
          confirmButtonText: '확인'
        });
        return false;
      }

      this.$emit('select-plan', {
        selling_krw:   plan.selling_krw,
        cost_krw:      plan.cost_krw,
        cancel_policy: plan.cancel_policy,
        booking_key:   plan.booking_key,
        rtxPackage:    plan.rtxPackage,
        rtxSearch:     plan.rtxSearch,
      });
    },

    krw(val) {
      if (val == null) return '-';
      return new Intl.NumberFormat('ko-KR').format(val);
    }
  }
};
</script>

<style scoped>
.rp-wrap {
  max-width: 1200px; margin: 0 auto;
  padding: 24px 16px; font-family: 'Noto Sans KR', sans-serif;
}

/* 헤더 */
.rp-header {
  display: flex; align-items: center; gap: 16px; margin-bottom: 20px;
}
.rp-back-btn {
  padding: 8px 16px; background: #f1eeee81; border: 2px solid #1c4915;
  border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
  color: #125751; transition: all 0.15s; white-space: nowrap;
}
.rp-back-btn:hover { background: #044051; color: #eeeaea; }
.rp-header-title { display: flex; align-items: center; gap: 8px; }
.rp-hotel-name { font-size: 20px; font-weight: 900; color: #1a1a2e; }
.hotel-grade-badge {
  font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 8px;
}
.grade-has  { background: #fff8e1; color: #b7791f; border: 1px solid #ded221; }
.grade-none { background: #f5f5f5; color: #aaa;    border: 1px solid #e0e0e0; }

/* 호텔 요약 */
/* 호텔 요약 레이아웃 조정 */
.rp-hotel-summary {
  display: flex; gap: 0; background: #fff; border-radius: 14px;
  border: 1px solid #e8edf2; overflow: hidden; margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  align-items: stretch;        /* ← 추가 */
  height: 300px;               /* ← 고정 높이 */
}

.rp-hero-img-wrap { width: 260px; flex-shrink: 0; }
.rp-hero-img { width: 100%; height: 100%; object-fit: cover; display: block; min-height: 160px; }
.rp-hero-placeholder {
  width: 100%; min-height: 160px; display: flex;
  align-items: center; justify-content: center; background: #f0f4f8; font-size: 48px;
}
/* 호텔 메타: 스크롤 허용 */
.rp-hotel-meta {
  padding: 20px; display: flex; flex-direction: column; gap: 8px; flex: 1;
  height: 300px;               /* ← 고정 */
  box-sizing: border-box;
}
.rp-hotel-name-big { font-size: 18px; font-weight: 800; color: #1a1a2e; }
.rp-hotel-name-orig { font-size: 13px; font-weight: 400; color: #888; margin-left: 6px; }
.rp-hotel-addr { font-size: 13px; color: #666; }
.rp-rating-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: #1a6eb5; border-radius: 6px 6px 6px 0; padding: 5px 10px; width: fit-content;
}
.rp-rating-score { font-size: 18px; font-weight: 900; color: #fff; }
.rp-rating-label { font-size: 10px; color: rgba(255,255,255,0.8); }

/* 조건 바 */
.rp-condition-bar {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  background: #f0f4ff; border-radius: 10px; padding: 10px 16px;
  margin-bottom: 24px; font-size: 13px; font-weight: 600; color: #3b5bdb;
}
.rp-cond-sep { color: #c5cfe8; }
.rp-cond-item { white-space: nowrap; }

/* 로딩 */
.rp-loading { display: flex; justify-content: center; padding: 60px 0; }
.rp-loading-inner {
  text-align: center; padding: 40px 60px; background: #fff;
  border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}
.rp-loading-anim {
  display: flex; justify-content: center; align-items: flex-end;
  gap: 5px; height: 40px; margin-bottom: 16px;
}
.ld-bar {
  width: 6px; background: #2e75b6; border-radius: 3px;
  animation: ldwave 0.8s ease-in-out infinite alternate;
}
@keyframes ldwave { from { height: 8px; opacity: 0.4; } to { height: 36px; opacity: 1; } }
.rp-loading-inner p { color: #555; font-size: 14px; margin: 0; }

/* 에러/빈값 */
.rp-error, .rp-empty { text-align: center; padding: 60px; }
.rp-error-icon, .rp-empty-icon { font-size: 48px; margin-bottom: 12px; }
.rp-retry-btn, .rp-back-btn2 {
  margin-top: 16px; padding: 10px 24px; background: #1a6eb5;
  color: #fff; border: none; border-radius: 8px; font-size: 14px;
  font-weight: 700; cursor: pointer;
}

/* 객실 카드 */
.rp-room-list { display: flex; flex-direction: column; gap: 16px; }
.rp-result-count { font-size: 14px; color: #555; margin-bottom: 8px; }
.rp-result-count strong { color: #1a1a2e; font-size: 16px; }

.rp-room-card {
  background: #fff; border: 1px solid #e8edf2; border-radius: 14px;
  overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;
}
.rp-room-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.10); }

/* ✅ 3단 레이아웃 */
.rp-room-body {
  display: flex; align-items: stretch; min-height: 220px;
}

/* 좌: 이미지 */
.rp-room-img-col {
  width: 220px; flex-shrink: 0; position: relative;
  background: #f0f4f8; overflow: hidden;
}
.rp-room-img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 0.3s;
  cursor: pointer;
}
.rp-room-card:hover .rp-room-img { transform: scale(1.04); }
.rp-room-img-placeholder {
  width: 100%; height: 100%; display: flex;
  align-items: center; justify-content: center; font-size: 48px; color: #ccc;
}
.rp-room-img-count {
  position: absolute; bottom: 8px; right: 8px;
  background: rgba(0,0,0,0.6); color: #fff;
  font-size: 11px; font-weight: 700;
  padding: 3px 8px; border-radius: 12px;
  pointer-events: none;
}

/* 중: 객실 정보 */
.rp-room-info-col {
  flex: 1; padding: 20px; display: flex; flex-direction: column;
  gap: 10px; border-right: 1px solid #f0f0f0;
}
.rp-room-name {
  font-size: 16px; font-weight: 800; color: #1a1a2e; line-height: 1.3;
}
.rp-room-bed-type {
  font-size: 13px; color: #3b5bdb; font-weight: 600;
}
.rp-room-attrs {
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px;
}
.rp-attr {
  font-size: 12px; font-weight: 600; color: #555;
  background: #f5f7fa; border-radius: 6px; padding: 3px 10px;
  display: flex; align-items: center; gap: 4px;
}
.rp-attr-smoking { background: #fff5f5; color: #c0392b; }

.rp-room-beds {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.rp-bed-tag {
  font-size: 12px; background: #eef2ff; color: #3b5bdb;
  border-radius: 6px; padding: 3px 10px; font-weight: 600;
}
.rp-plan-count-badge {
  margin-top: auto; font-size: 12px; font-weight: 700;
  color: #86cd53; background: #f0fff0; border-radius: 6px;
  padding: 4px 10px; width: fit-content;
}

/* 우: 플랜 목록 */
.rp-room-plans-col {
  width: 280px; flex-shrink: 0;
  display: flex; flex-direction: column;
}
.rp-plan-row {
  flex: 1; padding: 16px; border-bottom: 1px dashed #f0f0f0;
  display: flex; flex-direction: column; gap: 6px;
}
.rp-plan-row:last-child { border-bottom: none; }

.rp-plan-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.plan-tag {
  font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 20px;
}
.pt-food   { background: #fff8e6; color: #c47f00; }
.pt-r      { background: #d4edda; color: #155724; }
.pt-nr     { background: #f8d7da; color: #721c24; }
.pt-cancel { background: #f0fff4; color: #276749; font-size: 10px; }
.rp-plan-extra { font-size: 11px; color: #bbb; }

.rp-plan-price-wrap {
  margin-top: auto; display: flex; flex-direction: column;
  gap: 2px; align-items: flex-end;
}
.rp-plan-margin-badge { font-size: 11px; font-weight: 800; color: #86cd53; }
.rp-plan-cost { display: flex; gap: 6px; align-items: center; font-size: 11px; }
.rp-cost-label { color: #aaa; }
.rp-cost-val   { color: #bbb; text-decoration: line-through; }
.rp-plan-selling {
  font-size: 22px; font-weight: 900; color: #1a1a2e;
  letter-spacing: -0.5px; line-height: 1.1;
}
.rp-plan-selling small { font-size: 12px; font-weight: 500; }
.rp-plan-per-night  { font-size: 11px; color: #999; }
.rp-plan-margin-line { font-size: 12px; color: #555; }
.rp-plan-margin-line strong { color: #86cd53; }

.rp-book-btn {
  margin-top: 8px; padding: 9px 18px; background: #1a6eb5; color: #fff;
  border: none; border-radius: 8px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.2s; white-space: nowrap; width: 100%;
}
.rp-book-btn:hover { background: #86cd53; color: #fff; }

/* 반응형 */
@media (max-width: 768px) {
  .rp-room-body { flex-direction: column; }
  .rp-room-img-col { width: 100%; height: 200px; }
  .rp-room-plans-col { width: 100%; }
  .rp-plan-price-wrap { align-items: flex-start; }
}

/**
  * 호텔종합요약
**/

/* 갤러리 그리드 */
.rp-gallery-wrap {
  display: flex; gap: 4px; flex-shrink: 0;
  width: 560px;
  height: 300px;               /* ← summary와 동일 */
  min-height: unset;           /* ← min-height 제거 */
  border-radius: 14px 0 0 14px; overflow: hidden;
  align-self: stretch;         /* ← 추가 */
}
/* 메인 이미지 */
.rp-gallery-main {
  flex: 1.6; position: relative; cursor: pointer; overflow: hidden;
  min-height: 0;
}
.rp-gallery-main-img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 0.3s;
}
.rp-gallery-main:hover .rp-gallery-main-img { transform: scale(1.03); }
.rp-gallery-main-overlay {
  position: absolute; bottom: 10px; left: 10px;
  background: rgba(0,0,0,0.55); color: #fff; font-size: 12px; font-weight: 700;
  padding: 5px 12px; border-radius: 20px; backdrop-filter: blur(4px);
  opacity: 0; transition: opacity 0.2s;
}
.rp-gallery-main:hover .rp-gallery-main-overlay { opacity: 1; }

.rp-gallery-grid {
  flex: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 4px;
}
.rp-gallery-thumb {
  position: relative; cursor: pointer; overflow: hidden; background: #f0f4f8;
}
.rp-gallery-thumb-img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 0.25s;
}
.rp-gallery-thumb:hover .rp-gallery-thumb-img { transform: scale(1.06); }
.rp-gallery-more-badge {
  position: absolute; inset: 0; background: rgba(0,0,0,0.5);
  color: #fff; font-size: 14px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}

/* 갤러리 모달 */
.rp-gallery-modal {
  position: fixed; inset: 0; background: rgba(0,0,0,0.92);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; gap: 16px;
}
.rp-modal-img-wrap { position: relative; max-width: 80vw; }
.rp-modal-img {
  max-width: 80vw; max-height: 85vh; object-fit: contain;
  border-radius: 8px; display: block;
}
.rp-modal-counter {
  position: absolute; bottom: -28px; left: 50%; transform: translateX(-50%);
  color: rgba(255,255,255,0.7); font-size: 13px; font-weight: 600;
}
.rp-modal-close {
  position: absolute; top: 20px; right: 24px;
  background: rgba(255,255,255,0.15); color: #fff; border: none;
  border-radius: 50%; width: 36px; height: 36px; font-size: 18px;
  cursor: pointer; z-index: 10000;
}
.rp-modal-prev, .rp-modal-next {
  background: rgba(255,255,255,0.15); color: #fff; border: none;
  border-radius: 50%; width: 48px; height: 48px; font-size: 28px;
  cursor: pointer; flex-shrink: 0; transition: background 0.2s;
}
.rp-modal-prev:hover, .rp-modal-next:hover { background: rgba(255,255,255,0.3); }

.rp-hotel-meta { padding: 20px; display: flex; flex-direction: column; gap: 8px; flex: 1; }

.rp-hotel-desc {
  margin-top: 12px; padding: 12px; background: #f9f9f9; border-radius: 8px;
  font-size: 12px; color: #555; line-height: 1.4; max-height: 145px; overflow-y: auto;
}

@media (max-width: 768px) {
  .rp-gallery-wrap { width: 100%; height: 200px; border-radius: 0; }
  .rp-hotel-summary { flex-direction: column; }
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .rp-hotel-summary { flex-direction: column; }
  .rp-hero-img-wrap { width: 100%; }
  .rp-plan-row { flex-direction: column; align-items: flex-start; }
  .rp-plan-price-wrap { width: 100%; text-align: left; align-items: flex-start; }
}

/**
  * 위치정보 구글맵   
*/
.rp-map-wrap {
  margin-bottom: 16px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e8edf2;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.rp-hotel-map {
  width: 100%;
  height: 300px;
}

/**
  * 호텔 부대시설
*/
.rp-facilities-wrap {
  background: #fff; border-radius: 14px;
  border: 1px solid #e8edf2; padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.rp-facilities-title {
  font-size: 15px; font-weight: 800; color: #1a1a2e; margin-bottom: 12px;
}
.rp-facilities-list {
  display: flex; flex-wrap: wrap; gap: 8px;
}
.rp-facility-tag {
  font-size: 12px; font-weight: 600; color: #3e3bdb;
  background: #eef2ff; border-radius: 20px;
  padding: 4px 12px;
}

/**
  * 최소가 보장
*/
.pt-min { background: #fff3cd; color: #856404; }
</style>